// Cloud SQL 資料庫服務層
import { authenticatedFetch } from './apiClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://teleworker-api-5ubu67iiga-de.a.run.app';

// 基礎查詢介面
export interface QueryOptions {
  select?: string[];
  where?: Record<string, unknown>;
  orderBy?: { field: string; direction: 'asc' | 'desc' }[];
  limit?: number;
  offset?: number;
}

// 資料庫服務類
export class DatabaseService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  // 查詢單一表
  async query<T = unknown>(
    table: string,
    options: QueryOptions = {}
  ): Promise<T[]> {
    // 使用 AbortController 實現超時控制
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 秒超時

    try {
      const response = await authenticatedFetch(`${this.baseUrl}/api/db/query`, {
        method: 'POST',
        body: JSON.stringify({ table, ...options }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `查詢失敗: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || '查詢失敗');
      }

      return data.data || [];
    } catch (error) {
      clearTimeout(timeoutId);
      
      // 處理超時錯誤
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('請求超時：後端伺服器可能未運行或無法連接。請確認後端伺服器已啟動（http://localhost:8080）');
      }
      
      // 處理網路錯誤
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`無法連接到後端伺服器 (${this.baseUrl})。請確認後端伺服器已啟動。`);
      }
      
      throw error;
    }
  }

  // 取得單一記錄
  async getById<T = unknown>(table: string, id: string): Promise<T | null> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await authenticatedFetch(`${this.baseUrl}/api/db/${table}/${id}`, {
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 404) return null;
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `取得記錄失敗: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        return null;
      }

      return data.data || null;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('請求超時：後端伺服器可能未運行或無法連接。請確認後端伺服器已啟動（http://localhost:8080）');
      }
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`無法連接到後端伺服器 (${this.baseUrl})。請確認後端伺服器已啟動。`);
      }
      
      throw error;
    }
  }

  // 建立記錄
  async create<T = unknown>(table: string, data: Partial<T>): Promise<T> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/db/${table}`, {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `建立記錄失敗: ${response.statusText}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.message || result.error || '建立記錄失敗');
    }

    return result.data;
  }

  // 更新記錄
  async update<T = unknown>(table: string, id: string, data: Partial<T>): Promise<T> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/db/${table}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `更新記錄失敗: ${response.statusText}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.message || result.error || '更新記錄失敗');
    }

    return result.data;
  }

  // 刪除記錄
  // 將 404（記錄已不存在）視為等同成功的 no-op，避免儲存流程的競態條件
  // （例如「先全刪再全建」時，重試或重複觸發會嘗試刪除已不存在的記錄）。
  async delete(table: string, id: string): Promise<void> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/db/${table}/${id}`, {
      method: 'DELETE'
    });

    if (response.status === 404) {
      return;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `刪除記錄失敗: ${response.statusText}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || '刪除記錄失敗');
    }
  }

  // 批次操作
  async batch(operations: Array<{
    type: 'create' | 'update' | 'delete';
    table: string;
    id?: string;
    data?: unknown;
  }>): Promise<unknown[]> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/db/batch`, {
      method: 'POST',
      body: JSON.stringify({ operations })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `批次操作失敗: ${response.statusText}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || '批次操作失敗');
    }

    return result.data || [];
  }
}

// 匯出單例
export const db = new DatabaseService();


